import { findGuestIndexInArray, isGuestAlreadyInArray } from '@utils/helpers';
import { some } from '@utils/some';
import { Guest } from 'shared-types/Basket';

export const displayedGuests = (
  availableGuests: Guest[],
  isGenderSpecific?: string | boolean
): Guest[] =>
  availableGuests.filter((guest) => {
    if (isGenderSpecific === 'M') {
      return guest.gender === 'male';
    }
    if (isGenderSpecific === 'F') {
      return guest.gender === 'female';
    }
    return true;
  });

export const handleGuestRemove = (
  guest: Guest,
  fields: any,
  selectedGuests: Guest[],
  minimumGuests?: number | null,
  defaultGender?: string
): void => {
  const guestIndex = findGuestIndexInArray(selectedGuests, guest);

  if (guestIndex >= 0) {
    fields.remove(guestIndex);
    if (minimumGuests && fields.length <= minimumGuests) {
      fields.push({
        firstName: '',
        lastName: '',
        gender: defaultGender,
        room: 1,
      });
    }
  }
};

export const handleGuestAdd = (
  { title, firstName, lastName, gender, externalSpaId, id, loggedInUser }: Guest,
  fields: any,
  maximumGuests?: number | null
): void => {
  let emptyFieldIndex = -1;
  const allFields = fields.getAll();

  // Detect if this is a multi-room booking by checking if any field has room 2
  const isMultiRoom = some(allFields, (field: any) => field.room === 2);

  if (isMultiRoom) {
    // Multi-room booking logic

    // Find first empty field that belongs to room 1
    // We only add guests to room 1 fields in multi-room scenario
    for (let i = 0; i < fields.length; i += 1) {
      const guest = fields.get(i);
      if (!guest.firstName && !guest.lastName && guest.room === 1) {
        emptyFieldIndex = i;
        break;
      }
    }

    // Count how many guests are already in room 1
    // Only count fields that have actual guest details
    const currentRoom1Guests = allFields.filter(
      (guest) => guest.room === 1 && guest.firstName && guest.lastName
    ).length;

    // Check if we can add another guest based on room 1's maximum capacity
    const canAddGuest =
      !maximumGuests || (maximumGuests && currentRoom1Guests < (maximumGuests ?? 0));

    // If we found an empty field and haven't hit the max guests
    if (emptyFieldIndex >= 0 && canAddGuest) {
      // Remove the empty field and insert the new guest
      // Always setting room to 1 ensures guest stays in room 1
      fields.remove(emptyFieldIndex);
      fields.insert(emptyFieldIndex, {
        externalSpaId,
        ...(id !== loggedInUser?.id && { id }),
        title,
        firstName,
        lastName,
        gender,
        loggedInUser,
        room: 1,
      });
    }
  } else {
    // Single room booking logic
    // Find first empty field, ignoring room numbers
    for (let i = 0; i < fields.length; i += 1) {
      const guest = fields.get(i);
      if (!guest.firstName && !guest.lastName) {
        emptyFieldIndex = i;
        break;
      }
    }

    // For single room, check against total fields
    const canAddNextField = !maximumGuests || (maximumGuests && fields.length < maximumGuests);

    if (emptyFieldIndex >= 0 && (maximumGuests ? emptyFieldIndex < maximumGuests : true)) {
      // Replace empty field with new guest
      fields.remove(emptyFieldIndex);
      fields.insert(emptyFieldIndex, {
        externalSpaId,
        ...(id !== loggedInUser?.id && { id }),
        title,
        firstName,
        lastName,
        gender,
        loggedInUser,
      });
    } else if (canAddNextField) {
      // If no empty field but can add more, push new guest
      fields.push({
        externalSpaId,
        ...(id !== loggedInUser?.id && { id }),
        title,
        firstName,
        lastName,
        gender,
        loggedInUser,
      });
    }
  }
};

export const handleGuestPillClick = (
  guest: Guest,
  fields: any,
  selectedGuests: Guest[],
  minimumGuests?: number | null,
  maximumGuests?: number | null,
  defaultGender?: string
) => {
  if (isGuestAlreadyInArray(selectedGuests, guest)) {
    handleGuestRemove(guest, fields, selectedGuests, minimumGuests, defaultGender);
  } else {
    handleGuestAdd(guest, fields, maximumGuests);
  }
};
